import {
  Badge,
  Box,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { IAdGroup, IAdGroupDisplay, ICampaignFormField } from "../../App/Typings/Campaigns";

export function JobAlertsAdGroupDisplay({
  field,
  onChange,
}: {
  field: ICampaignFormField;
  onChange: () => void;
}) {
  const [checked, setChecked] = useState<string[]>(
    field.adGroupOptions?.filter((c) => c.enabled).map((c) => c.id) ?? [],
  );

  if (field.fieldType !== "interestPicker" || !field.adGroupOptions || field.adGroupOptions.length === 0) {
    return null;
  }

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    if (checked.length > 1 || currentIndex === -1) {
      field.adGroupOptions?.filter((c) => c.id === value).forEach((d) => (d.enabled = currentIndex === -1));

      setChecked(newChecked);
      onChange();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        padding: "0px 10px 0px 10px",
      }}
    >
      <Divider sx={{ mb: "10px" }} />
      <Box sx={{ mb: 1 }}>
        <h5 style={{ marginBottom: 0, marginTop: 0 }}>Available Dropdown Options:</h5>
      </Box>
      {getAdGroupDisplayList(field.adGroupOptions).map((adGroupOption, index) => (
        <Box
          key={adGroupOption.label}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mb: 2,
            p: 1,
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 1,
          }}
        >
          <Box
            key={adGroupOption.label}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ mr: 2, pl: 2 }}>
              {adGroupOption.label}
            </Typography>
            <Badge badgeContent={adGroupOption.options.length} color="secondary" />
          </Box>
          <Divider />
          <List dense>
            {adGroupOption.options.map((adGroup) => (
              <ListItem key={adGroup.id} dense disablePadding>
                <ListItemButton role={undefined} onClick={handleToggle(adGroup.id)} dense>
                  <ListItemIcon>
                    <Checkbox
                      disabled={checked.length <= 1 && checked.includes(adGroup.id)}
                      edge="start"
                      checked={checked.indexOf(adGroup.id) !== -1}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText>{adGroup.name}</ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}

function getAdGroupDisplayList(adGroups: IAdGroup[]) {
  const adGroupDisplayList = [] as IAdGroupDisplay[];

  for (const adGroup of adGroups) {
    const adGroupDisplay = adGroupDisplayList.find(
      (selectOptionGroup) => selectOptionGroup.label === adGroup.type,
    );
    if (adGroupDisplay) {
      const option = adGroupDisplay.options.find((group) => isAdGroupMatch(group, adGroup));
      if (!option) {
        adGroupDisplay.options.push(adGroup);
      }
    } else {
      adGroupDisplayList.push({
        label: adGroup.type,
        options: [adGroup],
        enabled: true,
      });
    }
  }

  for (const adGroupDisplay of adGroupDisplayList) {
    adGroupDisplay.options.sort((a, b) => a.name.localeCompare(b.name));
  }

  return adGroupDisplayList;
}

function isAdGroupMatch(option?: IAdGroup, testGroup?: IAdGroup | null) {
  return (
    option?.id === testGroup?.id || (option?.name === testGroup?.name && option?.type === testGroup?.type)
  );
}
